'use client'

import { HOUSE_FILTER_ALIAS } from '@config'
import { RESIDENTIAL_FILTER } from '@homeSearch/modules/filter/config'
import useFetch from '@hooks/useFetch'
import useProgressRouter from '@hooks/useProgressRouter'
import { searchResultDataLayer } from '@plugin/google/gtm/modules/houseList'
import { filterToParams, filterToString } from '@plugin/searchRoute/modules/house'
import { houseList } from '@router'
import { houseSearchType } from '@routerConfig'
import { global } from '@store/index'
import { toNumber } from '@utils'
import classNames from 'classnames'
import { assign, each, findIndex, get, isEmpty, isEqual, isFunction, keys, map, omit, size, sortBy, split } from 'lodash-es'
import { useCallback, useMemo, useState } from 'react'
import { useBoolean, useList } from 'react-use'
import { useRecoilValue } from 'recoil'
import useHouseSearchContext from '../../../context'
import { PopupContent, getFilterRouter, getFilterTotals } from '../../base'
import useParamsToFilter from '../../useParams2filter'
import MoreFilterContext from './context'
import FilterItemKeywords from './keywords'
import LivingOptions from './livingOptions'
import FilterItemNormal from './normal'
import FilterItemPrice from './price'
import FilterItemSurrounding from './surrounding'


export default function MoreFilterContent({
  onClose
}) {

  const { isMobile } = useRecoilValue(global)
  const { state, routeParams, filterMeta, surrounding, description, toggleSurrounding, setDescription } = useHouseSearchContext()
  const [filter, query, baseData] = useParamsToFilter({ filterMeta, state })
  const isResidential = state.type === houseSearchType.residential
  // 默认url上的filter数据
  const routeFilter = useMemo(() => {
    let res = {}
    if (!filter) return res
    const arr = split(filter, '_')
    for (let i = 0; i < arr.length; i += 2) {
      const key = arr[i]
      const val = map(split(arr[i + 1], ','), item => toNumber(item))
      res[key] = val
    }
    return res
  }, [filter])
  // 路由上的price数据，用来显示选中效果以及文字内容
  const routePrice = useMemo(() => {
    let { price } = routeParams
    if (!price) return
    price = decodeURIComponent(price)
    const arr = price.includes('-') ? split(price, '-') : split(price, '+')
    return map(arr, item => toNumber(item))
  }, [routeParams])
  // 当前选中的filter
  const [selectFilter, setSelectFilter] = useState(routeFilter)
  // 当前选中的price
  const [selectPrice, { set: setPrice, reset: resetPrice }] = useList(routePrice)
  // 单个选项点击事件
  const changeValue = useCallback((item, single = false) => {
    const val = toNumber(item.value)
    let list = [...get(selectFilter, item.alias, [])]
    const idx = findIndex(list, v => val === v)

    if (single) {
      if (idx === -1) {
        list = [val]
      }
    } else {
      if (idx === -1) {
        if (!item.num) return
        list.push(val)
      } else {
        list.splice(idx, 1)
      }
    }
    list = sortBy(list)
    setSelectFilter(d => {
      if (list.length === 0) {
        return omit(d, item.alias)
      }
      return ({ ...d, [item.alias]: list })
    })
  }, [selectFilter, setSelectFilter])
  // 清除单个选项
  const clearValueItem = useCallback((item) => {
    setSelectFilter(d => omit(d, item.alias))
  }, [setSelectFilter])
  // 根据当前选择生成参数，每次操作生成不同的参数
  const params = useMemo(() => {
    if (!query) return

    const filter = filterToString(selectFilter)
    return assign(
      omit(query, ['surrounding', 'description']),
      description ? { description } : undefined,
      surrounding ? { surrounding: 1 } : undefined,
      filter ? { filter } : undefined
    )
  }, [selectFilter, query, surrounding, description])
  // 根据动态参数，获取对应数据
  const { data, isLoading } = useFetch(params ? { url: RESIDENTIAL_FILTER.url, params } : null, {
    keepPreviousData: true
  })
  // 是否显示全部filter
  const [showAll, setShowAll] = useBoolean(!isResidential)
  const toggleShowAll = useCallback(() => setShowAll(), [setShowAll])
  // 显示的数据
  const viewData = useMemo(() => {
    if (!size(data)) return []

    if (showAll) return data

    const alias = [
      // sale
      'prn', 'bern', 'barn', 'ptn', 'tt', 'sm', 'fa', 'la',
      // rent
      'prrn', 'ptrn',
      // tag
      // 'tua'
    ]
    if (description) alias.push('tua')

    const d = keys(selectFilter).filter(key => size(selectFilter[key]) > 0)
    alias.push(...d)
    const res = data.filter(item => item.alias && alias.includes(item.alias))
    res.headers = data.headers

    return res
  }, [data, selectFilter, showAll, description])

  // 关闭弹窗事件
  const closeHandle = useCallback(() => {
    setSelectFilter(routeFilter)
    isFunction(onClose) && onClose()
  }, [onClose, routeFilter, setSelectFilter])
  // 清除所有参数
  const clearHandle = useCallback(() => {
    setSelectFilter()
    setPrice([])
    toggleSurrounding(false)
    setDescription('')
    searchResultDataLayer.filter.clear()
  }, [setSelectFilter, setPrice, toggleSurrounding, setDescription])
  // 确认显示
  const { replaceRouter } = useProgressRouter()
  const confirmHandle = useCallback(() => {
    const { slug, searchParams } = state
    const [query, filter] = filterToParams(selectFilter, baseData)
    const [, newSlug] = getFilterRouter(state)(houseRoute => {
      each(query, (val, key) => {
        houseRoute[key] = val
      })
      houseRoute.surround = surrounding
      houseRoute.description = description
      houseRoute.page = 1
    })

    if (!(isEqual(slug, newSlug) && filter === get(searchParams, 'filter', ''))) {
      replaceRouter(houseList(state.type, newSlug, assign({}, searchParams, { filter })))
    }
    searchResultDataLayer.filter.confirm()
    isFunction(onClose) && onClose()
  }, [selectFilter, baseData, state, replaceRouter, onClose, surrounding, description])

  return (
    <MoreFilterContext.Provider value={{ selectFilter, changeValue, clearValueItem, setSelectFilter, selectPrice, setPrice, baseData }}>
      <PopupContent
        className='flex flex-col h-fill-screen max-h-fill-screen !py-16 sm:!py-24 sm:px-24 sm:h-[640px] sm:max-h-[80vh]'
        contentClassName='flex-1 min-h-0 overflow-auto -mx-16 sm:-mx-24 px-16 sm:px-24'
        title={isMobile ? 'Select Filters' : 'More filters'}
        totals={isEmpty(selectFilter) && isEmpty(selectPrice) ? getFilterTotals(viewData) : getFilterTotals(viewData, false)}
        isLoading={isLoading}
        closeable
        onClose={closeHandle}
        onClear={clearHandle}
        onConfirm={confirmHandle}
      >
        <div className='space-y-20'>
          {
            map(viewData, item => (
              <FilterItem data={item} key={item.alias}></FilterItem>
            ))
          }
        </div>
        {
          isResidential && size(viewData) > 0 && (
            <div className='text-center mt-20'>
              <button className='button-text-primary text-base' onClick={toggleShowAll}>
                {showAll ? 'Less filters' : 'More filters'}
                <i className={classNames('icon icon-arrow-down ml-4 inline-block align-middle transition-transform', { 'rotate-180': showAll })}></i>
              </button>
            </div>
          )
        }
      </PopupContent>
    </MoreFilterContext.Provider>
  )
}

function FilterItem({
  data
}) {

  if (HOUSE_FILTER_ALIAS.price.includes(data.alias)) {
    return <FilterItemPrice data={data}></FilterItemPrice>
  }

  // Parking
  if (data.alias === 'pkn') {
    return (
      <>
        <FilterItemNormal data={data}></FilterItemNormal>
        <FilterItemSurrounding />
      </>
    )
  }

  // Tag
  if (data.alias === 'tua') {
    const d = { ...data, label: 'Keywords and tags' }
    return (
      <FilterItemNormal data={d}>
        <FilterItemKeywords />
      </FilterItemNormal>
    )
  }

  // living options
  if (data.alias === 'lo') {
    return (
      <LivingOptions data={data} />
    )
  }

  return <FilterItemNormal data={data}></FilterItemNormal>
}